import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { showError } from "../lib/errorHandler";
import PurchaseOrderConfirmDialog from "../components/PurchaseOrderConfirmDialog";
import {
  Upload,
  FileText,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Package,
  Calendar,
  User,
  Hash,
  Clock,
  RefreshCw,
  Sparkles,
  ClipboardList,
  Mail,
  Inbox,
  Send,
  ChevronDown,
  ChevronUp,
  XCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PurchaseOrders() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [emailStatus, setEmailStatus] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [extractedPayload, setExtractedPayload] = useState(null);

  const exportOrderPdf = (upload) => {
    const ed = upload.extracted_data || {};
    const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/logo-ep3d.png`;
    const items = ed.items || [];
    const itemsRows = items.map(i => `<tr><td>${i.part_code||"-"}</td><td>${i.description||"-"}</td><td style="text-align:right">${(i.quantity||0).toLocaleString()}</td><td style="text-align:right">${i.unit_price ? i.unit_price.toFixed(4)+" EUR" : "-"}</td><td style="text-align:right">${i.unit_price && i.quantity ? (i.unit_price*i.quantity).toFixed(2)+" EUR" : "-"}</td></tr>`).join("");
    const totalValue = items.reduce((s,i) => s + (i.unit_price||0) * (i.quantity||0), 0);
    const html = `<!DOCTYPE html><html><head><title>Nota Encomenda ${ed.order_number||upload.filename}</title>
    <style>@page{size:A4;margin:15mm} body{font-family:Arial,sans-serif;color:#1e293b;font-size:11px}
    .header{display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid #f59e0b;padding-bottom:10px;margin-bottom:20px}
    .header img{height:50px} h1{font-size:18px;color:#0f172a}
    .info{background:#f8fafc;padding:12px;border-radius:6px;margin-bottom:15px;display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:11px}
    .info .label{color:#64748b;font-weight:600} .info .value{color:#0f172a;font-weight:500}
    table{width:100%;border-collapse:collapse;margin:15px 0} th{background:#0f172a;color:#f59e0b;padding:6px 10px;text-align:left;font-size:10px}
    td{padding:5px 10px;border-bottom:1px solid #e5e7eb}
    .total{font-size:16px;font-weight:bold;text-align:right;margin:15px 0}
    .footer{margin-top:20px;border-top:1px solid #e5e7eb;padding-top:8px;color:#9ca3af;font-size:9px;text-align:center}
    </style></head><body>
    <div class="header"><img src="${logoUrl}" alt="EP3D" onerror="this.outerHTML='<b>EP3D Produção</b>'" /><div style="text-align:right;font-size:10px"><strong>NOTA DE ENCOMENDA</strong><br>${ed.order_number||"-"}</div></div>
    <h1>Nota de Encomenda — ${ed.order_number||upload.filename}</h1>
    <div class="info">
      <div><span class="label">Cliente:</span> <span class="value">${ed.client_name||"-"}</span></div>
      <div><span class="label">NIF:</span> <span class="value">${ed.client_nif||"-"}</span></div>
      <div><span class="label">Morada:</span> <span class="value">${ed.client_address||"-"}</span></div>
      <div><span class="label">Nº Encomenda:</span> <span class="value">${ed.order_number||"-"}</span></div>
      <div><span class="label">Data Encomenda:</span> <span class="value">${ed.order_date||"-"}</span></div>
      <div><span class="label">Data Entrega:</span> <span class="value">${ed.delivery_date||"-"}</span></div>
      <div><span class="label">Fonte:</span> <span class="value">${upload.source==="email"?"Email":"Upload Manual"}</span></div>
      <div><span class="label">Processado:</span> <span class="value">${upload.created_at ? new Date(upload.created_at).toLocaleString("pt-PT") : "-"}</span></div>
    </div>
    <table><tr><th>Código Peça</th><th>Descrição</th><th style="text-align:right">Quantidade</th><th style="text-align:right">Preço Unit.</th><th style="text-align:right">Total</th></tr>${itemsRows}</table>
    ${totalValue > 0 ? `<p class="total">Valor Total: ${totalValue.toFixed(2)} EUR</p>` : ""}
    <div class="footer">EP3D Produção — Nota de Encomenda — ${new Date().toLocaleString("pt-PT")}</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `NotaEncomenda_${ed.order_number||upload.id?.substring(0,8)||"doc"}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const toggleHistoryExpand = (id) => {
    setExpandedHistory(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem("mes_token");
      const res = await axios.get(`${API}/purchase-orders/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.uploads || []);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchEmailStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("mes_token");
      const res = await axios.get(`${API}/purchase-orders/email-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailStatus(res.data);
    } catch (error) {
      console.error("Error fetching email status:", error);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchEmailStatus();
  }, [fetchHistory, fetchEmailStatus]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error("Apenas ficheiros PDF são aceites");
        return;
      }
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Selecione um ficheiro PDF");
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem("mes_token");
      const formData = new FormData();
      formData.append("file", selectedFile);

      // NOVO FLUXO: extrair primeiro (sem criar nada), depois abrir modal de confirmação
      const res = await axios.post(`${API}/purchase-orders/extract`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setExtractedPayload(res.data);
      setConfirmDialogOpen(true);
      toast.info("Dados extraídos — confirma antes de criar as ordens.");
    } catch (error) {
      console.error("Error extracting:", error);
      showError(error, "Erro ao extrair dados do PDF");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmed = (result) => {
    setUploadResult({
      total_orders_created: result.created_orders.length,
      created_orders: result.created_orders,
      errors: result.errors,
      client_action: result.client_action,
    });
    fetchHistory();
    setSelectedFile(null);
    setExtractedPayload(null);
  };

  const checkEmailNow = async () => {
    try {
      setCheckingEmail(true);
      const token = localStorage.getItem("mes_token");
      const res = await axios.post(`${API}/purchase-orders/check-email`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mostrar resultado detalhado
      const result = res.data;
      if (result.processed > 0) {
        toast.success(`${result.processed} PDF(s) processado(s) com sucesso!`);
      } else if (result.skipped > 0) {
        toast.info(`${result.skipped} email(s) já processados anteriormente`);
      } else {
        toast.info(result.message || "Nenhum email novo encontrado");
      }
      
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} erro(s) no processamento`);
      }
      
      fetchHistory();
      fetchEmailStatus();
    } catch (error) {
      console.error("Error checking email:", error);
      showError(error, "Erro ao verificar email");
    } finally {
      setCheckingEmail(false);
    }
  };

  const reprocessEmails = async () => {
    try {
      setCheckingEmail(true);
      const token = localStorage.getItem("mes_token");
      const res = await axios.post(`${API}/purchase-orders/reprocess-emails?days_back=7`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = res.data;
      if (result.processed > 0) {
        toast.success(`${result.processed} PDF(s) reprocessado(s)!`);
      } else if (result.total_emails_checked > 0) {
        toast.info(`${result.total_emails_checked} emails verificados, ${result.skipped} já processados`);
      } else {
        toast.info("Nenhum email encontrado nos últimos 7 dias");
      }
      
      if (result.errors?.length > 0) {
        toast.error(`${result.errors.length} erro(s): ${result.errors[0]}`);
      }
      
      fetchHistory();
      fetchEmailStatus();
    } catch (error) {
      console.error("Error reprocessing emails:", error);
      showError(error, "Erro ao reprocessar emails");
    } finally {
      setCheckingEmail(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="purchase-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-foreground flex items-center gap-3">
            <FileText className="h-8 w-8 text-primary" />
            Notas de Encomenda
          </h1>
          <p className="text-muted-foreground mt-1">
            Upload de notas de encomenda em PDF ou receção automática por email
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchHistory(); fetchEmailStatus(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Email Integration Status */}
      {emailStatus && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <Mail className="h-5 w-5" />
              Integração de Email
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <Inbox className="h-4 w-4 text-blue-500" />
                  <span>Email:</span>
                  <strong className="font-mono">{emailStatus.config?.email_address}</strong>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <span>Verificação automática a cada:</span>
                  <strong>{emailStatus.config?.check_interval_minutes} minutos</strong>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Send className="h-4 w-4 text-blue-500" />
                  <span>Encomendas recebidas por email:</span>
                  <Badge variant="secondary">{emailStatus.total_email_uploads || 0}</Badge>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={emailStatus.config?.scheduler_active ? "bg-green-500" : "bg-red-500"}>
                  {emailStatus.status}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={checkEmailNow}
                  disabled={checkingEmail}
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  data-testid="check-email-btn"
                >
                  {checkingEmail ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Verificar Novos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reprocessEmails}
                  disabled={checkingEmail}
                  className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  data-testid="reprocess-email-btn"
                >
                  {checkingEmail ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Reprocessar (7 dias)
                </Button>
              </div>
            </div>
            <div className="mt-3 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Como usar:</strong> Envie um email com o PDF da nota de encomenda anexado para{" "}
                <strong className="font-mono">{emailStatus.config?.email_address}</strong>. 
                O sistema irá processar automaticamente e criar as ordens.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-blue-500" />
              Upload Manual de PDF
            </CardTitle>
            <CardDescription>
              Carregue um ficheiro PDF e a IA irá extrair os dados e criar as ordens automaticamente
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
                id="pdf-upload"
                data-testid="pdf-upload-input"
              />
              <label htmlFor="pdf-upload" className="cursor-pointer">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-600">
                  {selectedFile ? selectedFile.name : "Clique para selecionar PDF"}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  Apenas ficheiros PDF são aceites
                </p>
              </label>
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-800">{selectedFile.name}</p>
                    <p className="text-sm text-blue-600">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="process-pdf-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      A processar...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Processar com IA
                    </>
                  )}
                </Button>
              </div>
            )}

            {uploading && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-3">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">A processar nota de encomenda...</p>
                    <p className="text-sm text-amber-600">
                      A IA está a extrair os dados do PDF. Isto pode demorar alguns segundos.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Section */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-green-500" />
              Resultado do Processamento
            </CardTitle>
            <CardDescription>
              Ordens criadas automaticamente a partir da nota de encomenda
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!uploadResult ? (
              <div className="text-center py-12 text-gray-400">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p>Carregue uma nota de encomenda para ver o resultado</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Extracted Data */}
                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <h4 className="font-bold text-gray-700 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    Dados Extraídos pela IA
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-gray-400" />
                      <span>Nº Encomenda:</span>
                      <strong>{uploadResult.extracted_data?.order_number || "N/A"}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span>Cliente:</span>
                      <strong>{uploadResult.extracted_data?.client_name || "N/A"}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-gray-400" />
                      <span>NIF:</span>
                      <strong>{uploadResult.extracted_data?.client_nif || "N/A"}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span>Data:</span>
                      <strong>{uploadResult.extracted_data?.order_date || "N/A"}</strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span>Entrega:</span>
                      <strong>{uploadResult.extracted_data?.delivery_date || "N/A"}</strong>
                    </div>
                  </div>
                </div>

                {/* Created Orders */}
                {uploadResult.created_orders?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-green-700 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Ordens Criadas ({uploadResult.created_orders.length})
                    </h4>
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {uploadResult.created_orders.map((order, idx) => (
                        <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-mono font-bold text-green-700">{order.order_code}</span>
                              <p className="text-sm text-green-600">
                                {order.part_description} ({order.part_code})
                              </p>
                            </div>
                            <div className="text-right">
                              <Badge className="bg-green-500">{order.quantity} un.</Badge>
                              <p className="text-xs text-green-600 mt-1">{order.estimated_days} dia(s)</p>
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-green-600">
                            <span>Início: {order.planned_start}</span>
                            <span className="mx-2">→</span>
                            <span>Fim: {order.planned_end}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {uploadResult.errors?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-bold text-red-700 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Erros ({uploadResult.errors.length})
                    </h4>
                    <div className="space-y-1">
                      {uploadResult.errors.map((error, idx) => (
                        <div key={idx} className="p-2 bg-red-50 rounded text-sm text-red-700">
                          {error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* History Section */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-gray-500" />
            Histórico de Processamento
          </CardTitle>
          <CardDescription>
            Notas de encomenda processadas (upload manual e email)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma nota de encomenda processada ainda</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((upload) => (
                <div
                  key={upload.id}
                  className={`rounded-lg border transition-colors ${
                    upload.errors?.length > 0 && (upload.created_orders?.length || 0) === 0
                      ? 'bg-red-50 border-red-200'
                      : upload.errors?.length > 0
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {/* Header - Clicável */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-black/5 transition-colors"
                    onClick={() => toggleHistoryExpand(upload.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {upload.source === 'email' ? (
                          <Mail className="h-8 w-8 text-blue-500" />
                        ) : (
                          <FileText className="h-8 w-8 text-purple-500" />
                        )}
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {upload.filename}
                            {upload.source === 'email' && (
                              <Badge variant="outline" className="text-xs">via email</Badge>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(upload.created_at).toLocaleString("pt-PT")}
                            {upload.source_email && (
                              <span className="ml-2 text-blue-600">de {upload.source_email}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">
                            Encomenda: <strong>{upload.extracted_data?.order_number || "N/A"}</strong>
                          </p>
                          <p className="text-sm text-gray-600">
                            Cliente: <strong>{upload.extracted_data?.client_name || "N/A"}</strong>
                            {upload.extracted_data?.client_nif && <span className="text-gray-400 ml-2">NIF: {upload.extracted_data.client_nif}</span>}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={(e) => { e.stopPropagation(); exportOrderPdf(upload); }}>
                            <FileText className="h-3 w-3" /> PDF
                          </Button>
                          {(upload.created_orders?.length || 0) > 0 && (
                            <Badge className="bg-green-500">
                              {upload.created_orders.length} ordens
                            </Badge>
                          )}
                          {upload.errors?.length > 0 && (
                            <Badge variant="destructive" className="flex items-center gap-1">
                              <XCircle className="h-3 w-3" />
                              {upload.errors.length} erros
                            </Badge>
                          )}
                          {(upload.created_orders?.length || 0) === 0 && (upload.errors?.length || 0) === 0 && (
                            <Badge variant="secondary">Sem dados</Badge>
                          )}
                        </div>
                        <div className="text-gray-400">
                          {expandedHistory[upload.id] ? (
                            <ChevronUp className="h-5 w-5" />
                          ) : (
                            <ChevronDown className="h-5 w-5" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Detalhes Expandidos */}
                  {expandedHistory[upload.id] && (
                    <div className="px-4 pb-4 border-t border-gray-200/50 pt-4 space-y-4">
                      {/* Dados Extraídos */}
                      <div className="p-3 bg-white/50 rounded-lg">
                        <h5 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-purple-500" />
                          Dados Extraídos pela IA
                        </h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Nº Encomenda:</span>
                            <p className="font-medium">{upload.extracted_data?.order_number || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Cliente:</span>
                            <p className="font-medium">{upload.extracted_data?.client_name || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Data:</span>
                            <p className="font-medium">{upload.extracted_data?.order_date || "N/A"}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Entrega:</span>
                            <p className="font-medium">{upload.extracted_data?.delivery_date || "N/A"}</p>
                          </div>
                        </div>
                        {upload.extracted_data?.items && upload.extracted_data.items.length > 0 && (
                          <div className="mt-3">
                            <span className="text-gray-500 text-sm">Itens extraídos:</span>
                            <div className="mt-1 space-y-1">
                              {upload.extracted_data.items.map((item, idx) => (
                                <div key={idx} className="text-sm p-2 bg-gray-100 rounded flex justify-between">
                                  <span><strong>{item.part_code}</strong> - {item.description || 'Sem descrição'}</span>
                                  <Badge variant="secondary">{item.quantity} un.</Badge>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Ordens Criadas */}
                      {upload.created_orders?.length > 0 && (
                        <div className="p-3 bg-green-100/50 rounded-lg">
                          <h5 className="font-semibold text-sm text-green-700 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4" />
                            Ordens Criadas ({upload.created_orders.length})
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {upload.created_orders.map((orderCode, idx) => (
                              <Badge key={idx} className="bg-green-600">
                                {typeof orderCode === 'string' ? orderCode : orderCode.order_code || orderCode}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Erros - ESTA É A PARTE PRINCIPAL */}
                      {upload.errors?.length > 0 && (
                        <div className="p-3 bg-red-100/50 rounded-lg border border-red-200">
                          <h5 className="font-semibold text-sm text-red-700 mb-2 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            Erros no Processamento ({upload.errors.length})
                          </h5>
                          <div className="space-y-2">
                            {upload.errors.map((error, idx) => (
                              <div 
                                key={idx} 
                                className="p-2 bg-red-50 rounded border border-red-200 text-sm text-red-800 flex items-start gap-2"
                              >
                                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                                <span>{error}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Processamento com Sucesso Total */}
                      {(upload.errors?.length || 0) === 0 && (upload.created_orders?.length || 0) > 0 && (
                        <div className="p-3 bg-green-100/50 rounded-lg flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <span className="text-green-700 font-medium">
                            Processamento concluído sem erros
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de confirmação — extracção → cliente → peças → criar */}
      {extractedPayload && (
        <PurchaseOrderConfirmDialog
          open={confirmDialogOpen}
          onClose={() => { setConfirmDialogOpen(false); setExtractedPayload(null); }}
          draftId={extractedPayload.draft_id}
          initialClient={{
            name: extractedPayload.extracted_data?.client_name || "",
            nif: extractedPayload.extracted_data?.client_nif || "",
            address: extractedPayload.extracted_data?.client_address || "",
            city: extractedPayload.extracted_data?.client_city || "",
            postal_code: extractedPayload.extracted_data?.client_postal_code || "",
            phone: extractedPayload.extracted_data?.client_phone || "",
            email: extractedPayload.extracted_data?.client_email || "",
            contact_person: extractedPayload.extracted_data?.client_contact_person || "",
          }}
          initialOrderNumber={extractedPayload.extracted_data?.order_number || ""}
          initialDeliveryDate={extractedPayload.extracted_data?.delivery_date || ""}
          itemsPreview={extractedPayload.items_preview || []}
          matchedClient={extractedPayload.matched_client}
          onConfirmed={handleConfirmed}
        />
      )}
    </div>
  );
}
